import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';

export interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
    locale: 'en' | 'zh' | null;
    translate_merchants: boolean;
  } | null;
}

/**
 * Reads /api/me on mount, syncs i18next language to the server-stored locale.
 * Mutations: setLocale(), setTranslateMerchants(). All persisted to /api/me/preferences.
 *
 * Call once near the top of the authenticated tree (App.tsx). Re-running it
 * is cheap (TanStack Query caches by key).
 */
export function useUserPreferences() {
  const { i18n } = useTranslation();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/me'),
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    const serverLocale = data?.user?.locale;
    if (serverLocale && serverLocale !== i18n.language) {
      void i18n.changeLanguage(serverLocale);
    }
  }, [data?.user?.locale, i18n]);

  const setLocale = useMutation({
    mutationFn: (locale: 'en' | 'zh' | null) =>
      api.patch<MeResponse>('/me/preferences', { locale }),
    onSuccess: async (res) => {
      qc.setQueryData(['me'], res);
      const next = res.user?.locale;
      if (next && next !== i18n.language) await i18n.changeLanguage(next);
    },
  });

  const setTranslateMerchants = useMutation({
    mutationFn: (translate_merchants: boolean) =>
      api.patch<MeResponse>('/me/preferences', { translate_merchants }),
    onSuccess: (res) => qc.setQueryData(['me'], res),
  });

  return {
    locale: (data?.user?.locale ?? i18n.language) as 'en' | 'zh',
    translateMerchants: data?.user?.translate_merchants ?? true,
    setLocale,
    setTranslateMerchants,
    isLoading: !data,
  };
}
